import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex';
import api from '@reserve/api'
import { uGetDateFormat, IEDate, uGetTimeArray } from '@appbase/util/date-helper'
import { FlowFileMeta } from '@appbase/components/common/FlowFileUploader/flow-file-meta'
import { invalidateUrl } from '@/modules/@appbase/util';
import * as types from '@reserve/store/types'
import * as accountTypes from '@account/store/types'
import { ReserveResourceMode } from '@reserve/types';
import { getFullTimetext } from '@reserve/disp-helper';
import { FlowFile } from '@/modules/@appbase/libs/flowjs';
import { handleClose, openReserveWindow } from '../../popup-handler';

import { LoadingBar } from '@appbase/components/loading';
import { EumEditor, ViewFrame } from '@appbase/components/editor'
import { FlowFileUploader } from '@appbase/components/common'
import RecipientField from '@reserve/components/RecipientField.vue';
import RecurringOption from './RecurringOption.vue';
import TimespanSelectionPane from '@reserve/components/ReserveResource/TimespanSelectionPane.vue';
import ResourceSelectionPane from '@reserve/components/ReserveResource/ResourceSelectionPane.vue';
import Attachments from './Attachments.vue';

@Component({
    components: {        
        TimespanSelectionPane,
        ResourceSelectionPane,
        RecipientField,
        FlowFileUploader,
        RecurringOption,
        EumEditor,
        ViewFrame,
        Attachments,
        LoadingBar
    },
    computed: mapGetters({
        user: accountTypes.USER,
        resourceList: types.EWS_RESOURCEGROUPS_LIST,
        resourceListDb: types.DB_RESOURCEGROUPS_LIST,
        selectedResources: types.SELECTED_RESOURCE_KEYS
    }),

    methods: mapActions({
        createEvent: types.CALENDAR_EVENT_CREATE,
        updateEvent: types.CALENDAR_EVENT_UPDATE,
        cancelEwsEvent: types.CALENDAR_EVENT_EWSCANCEL,
        cancelDbEvent: types.CALENDAR_EVENT_DBCANCEL,
        loadEvents: types.CALENDAR_EVENTLIST_LOAD
    })
})
export default class ReserveResource extends Vue {
    @Prop({ type: Boolean, default: false }) isPopup!: boolean; 

    createEvent!: (payload: { item: reserve.CalendarItem, itemStore: 'EWS' | 'DB' }) => Promise<any>;
    updateEvent!: (payload: { item: reserve.CalendarItem, itemStore: 'EWS' | 'DB', masterOccurrence: string | null }) => Promise<any>;
    cancelEwsEvent!: (id: string) => Promise<any>;
    cancelDbEvent!: (item: reserve.CalendarItem) => Promise<any>;
    loadEvents!: () => Promise<any[]>;

    user!: account.User;
    resourceList!: ewsResource.EwsResourceItem[];
    resourceListDb!: reserve.DbResourceItem[];
    selectedResources!: string[];

    mode: ReserveResourceMode = ReserveResourceMode.Write;
    contentLoaded: boolean = false;
    showRecurringOption: boolean = false;
    currentReccuringOption: reserve.CalendarItemRecurrence | null = null;
    recurrenceDesc: string = "";
    recurrenceIntervalDesc: string = "";
    dispResourceName: string = "";
    confSubject: string = "";

    myCalendarFolderId: string | null = null;
    currentItem: reserve.CalendarItem | null = null;  
    curResourceItem: ewsResource.EwsResourceItem | reserve.DbResourceItem | null = null;      
    masterOccurrence: string | null = null; 
    sequence: number = 0; 

    isAlldayEvent: boolean = false;

    eventStart: string = uGetDateFormat('yyyy-MM-dd', new Date());
    eventStartTime: string = "";
    eventEnd: string = uGetDateFormat('yyyy-MM-dd', new Date());
    eventEndTime: string = "";
    initCompany: string = '';
    initCategory: 'FAVORITE' | 'EWS' | 'DB' | null = null;
    initResource: string | null = null;
    dispEventTime: string = "";
    timeMounted: boolean = false;

    requiredRecipients: orgmap.UserItem[] = [];
    optionalRecipients: orgmap.UserItem[] = [];    

    uploadUrl: string = invalidateUrl('/cm/file/store/flow');
    deleteUrl: string = invalidateUrl('/cm/file/store/deleteFile');
    sizeLimit: number | null = null;
    bannedExtentions: string[] = [];
    files: FlowFile[] = [];
    ewsAttachments: ewsResource.EwsAttachment[] = [];
    isFileUploading: boolean = false;        
    isLoading: boolean = false;
    uploaderConfig: FlowFileMeta = {
        code: '',
        codeValues: [],
        type: 'file'
    };

    reserveContent: string = "";
    
    synapopts: editor.synap.SynapEditorConfig = {        
        'editor.autoSave': false,
        'editor.autoSave.period' : 10,
        'editor.menu.show' : true,                     
    }

    onTimespanChanged(startDate: string, startTime: string, endDate: string, endTime: string) {
        if(this.contentLoaded) {          
            this.updateRecurrenceDesc();
        } 
    }   

    created() {
        var { comCode, itemStore, reserveId, resource } = this.$route.query;
    
        if(comCode) {
            this.initCompany = comCode.toString();
        }

        if(itemStore) {
            this.initCategory = itemStore.toString() as 'FAVORITE' | 'EWS' | 'DB';
        }

        if(resource) {
            this.initResource = resource.toString();
        }

        if(reserveId && resource) {     
            api.resource.getResource(itemStore.toString(), resource.toString()).then(res => {
                this.curResourceItem = res as (ewsResource.EwsResourceItem | reserve.DbResourceItem | null);
                if(itemStore === 'EWS') {
                    this.mountEditModeEws(resource.toString(), reserveId.toString())
                } else {
                    this.mountEditDb(resource.toString(), reserveId.toString());
                }   
            });                                    
        } else {
            this.mountWriteMode();
        }
    }   

    mountWriteMode() {
        this.mode = ReserveResourceMode.Write;
        const uid = `uid-${Date.now()}`;
        this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
        this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;

        let promise = this.user.isMailUser ? 
            api.resourceEws.getMyCalendarFolderId() :
            Promise.resolve(null);

        promise.then(res => {                
                this.myCalendarFolderId = res;
                this.mountTimespan();
                this.contentLoaded = true;       
            });   
    }

    async mountEditModeEws(resource: string, reserveId: string) { 
        var { open } = this.$route.query;
        
        this.dispResourceName = this.curResourceItem ? this.curResourceItem.displayName : "";         
        this.masterOccurrence = open ? open.toString() : null;
        
        Promise.all([
            api.ewsCalendar.getDetail(reserveId, resource),
            api.ewsCalendar.getContent(reserveId, resource),           
        ]).then(res => {                                          
            this.currentItem = res[0] as reserve.CalendarItem;
            this.confSubject = this.currentItem.subject;
           
            let owner = this.currentItem.organizer.address?.toLowerCase() === this.user.email?.toLowerCase();              
            this.mode = owner ? ReserveResourceMode.Edit : ReserveResourceMode.Read

            const uid = `uid-${Date.now()}`;
            this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
            this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;
            
            
            var postProcess: Promise<any> = Promise.resolve();

            if(this.mode == ReserveResourceMode.Edit) {             
                var iCalUid = res[0].iCalUid;

                if(this.masterOccurrence != null && (this.masterOccurrence === 'occurrence' || this.masterOccurrence === 'exception')) {
                    postProcess = api.ewsCalendar.getOriginalAppointment(iCalUid, res[0].start, res[0].end);   
                } else {
                    postProcess = api.ewsCalendar.getOriginalAppointment(iCalUid);
                }                
            }

            postProcess.then(postRes => {             
                var mountItem = res[0];

                if(this.mode == ReserveResourceMode.Edit) {
                    if(postRes == null) {
                        this.mode = ReserveResourceMode.Read
                    } else {
                        mountItem = postRes.item;
                        this.currentItem = mountItem;
                        this.ewsAttachments = mountItem.attachments;
                        
                        // occurrence 또는 exception인 경우 실제 날짜/시간을 유지
                        if(this.masterOccurrence != null && (this.masterOccurrence === 'occurrence' || this.masterOccurrence === 'exception')) {
                            mountItem.start = res[0].start;
                            mountItem.end = res[0].end;
                        }
                    }
                } 
                                
                this.mountEditCallback(mountItem, res[1]);                                    
            }).catch(async (err) => {
                await (window as any).alertAsync(this.$t('예약자의_사서함에_존재하지않아_수정할_수_없습니다').toString());
                this.mode = ReserveResourceMode.Read
                this.mountEditCallback(res[0], res[1]);
            });
        }).catch(async (err) => {
            console.error(err);
            await (window as any).alertAsync(this.$t('취소된_일정').toString());
            handleClose(this.isPopup, true);
        })      
    }

    async mountEditDb(resource: string, reserveId: string) {
        var { open, sequence } = this.$route.query;        
        this.dispResourceName = this.curResourceItem ? this.curResourceItem.displayName : "";
        this.masterOccurrence = open ? open.toString() : null;
        this.sequence = sequence ? Number(sequence) : 0;
                
        let eventPromise: Promise<reserve.CalendarItem> = this.masterOccurrence === 'exception' ?
            api.resourceDb.getCalendarREvent(Number(sequence), Number(reserveId), this.user.personCode) :
            api.resourceDb.getCalendarEvent(Number(reserveId), this.user.personCode);
        
        eventPromise.then(res => {            
            this.currentItem = res;
            this.confSubject = res.subject;            

            let owner = this.currentItem.organizer.address?.toLowerCase() === this.user.email?.toLowerCase();              
            this.mode = owner ? ReserveResourceMode.Edit : ReserveResourceMode.Read

            const uid = `uid-${Date.now()}`;
            this.uploaderConfig.codeValues = ['reserve', `CO_${this.user.comCode}`, this.user.email, uid];
            this.uploaderConfig.code = `RESERVE_${this.user.id}_${uid}`;

            this.mountEditCallback(res, res.body);
        }).catch(async (err) => {
            console.error(err);
            await (window as any).alertAsync(this.$t('취소된_일정').toString());
            handleClose(this.isPopup, true);
        })
    }

    mountEditCallback(mountItem: reserve.CalendarItem, content: string) {        
        this.mountTimespan(mountItem);
        this.mountAttendees(mountItem);

        if(typeof(mountItem.recurrence) === 'object') {
            this.mountRecurrence(mountItem.recurrence);   
        }                   

        this.reserveContent = content;
        this.contentLoaded = true;              
    }

    mountTimespan(item?: reserve.CalendarItem) {   
        var { param } = this.$route.query;

        if(param) {
            var decode = atob(decodeURIComponent(param.toString()));
            var reserveParam: reserve.ReserveResourceParam = JSON.parse(decode);

            this.initCompany = reserveParam.comCode;
            this.eventStart = reserveParam.eventStart;
            this.eventEnd = reserveParam.eventEnd;
            this.eventStartTime = reserveParam.eventStartTime;
            this.eventEndTime = reserveParam.eventEndTime;   
            this.initCategory = reserveParam.itemStore;
            this.initResource = reserveParam.resource || null; 
        } else {    
            if(item) {
                this.isAlldayEvent = item.isAllDayEvent && item.isAllDayEvent.toString().toLowerCase() === 'true';
                this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(item.start));
                this.eventStartTime = uGetDateFormat('H:i:s', IEDate(item.start));
                this.dispEventTime = getFullTimetext(item);

                // if(this.isAlldayEvent)
                // {
                //     //종일 경우.. 날짜 혼란으로 달력으로 보일때는 -1로 보여준다. (종일로 저장시 +1)
                //     var endDate = new Date();
                //     endDate.setDate(IEDate(item.end).getDate() -1);
                //     endDate.setHours(23);
                //     endDate.setMinutes(30);
                //     endDate.setSeconds(0);
                //     this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(endDate)); // 날짜 -1
                //     this.eventEndTime = uGetDateFormat('H:i:s', IEDate(endDate)); // 시간 00:00 그대로
                    
                // }
                // else
                // {
                //alert("item.end=" + item.end);
                    this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(item.end));
                    this.eventEndTime = uGetDateFormat('H:i:s', IEDate(item.end));
                //}
            } else {
                var timeNow = new Date();
                if(timeNow.getMinutes() >= 30) {                  
                    timeNow.setHours(timeNow.getHours() + 1);
                    this.eventStartTime = uGetDateFormat("H:00:00", timeNow);  
                    this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));              
                    
                    timeNow.setHours(timeNow.getHours() + 1);                  
                    this.eventEndTime = uGetDateFormat("H:00:00", timeNow);
                    this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));
                } else {
                    this.eventStartTime = uGetDateFormat("H:30:00", timeNow);  
                    this.eventStart = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));   
                    timeNow.setHours(timeNow.getHours() + 1);         
                    this.eventEndTime = uGetDateFormat("H:30:00", timeNow);
                    this.eventEnd = uGetDateFormat('yyyy-MM-dd', IEDate(timeNow));
                }    
            }
        }                                   
    }   

    mountAttendees(item: reserve.CalendarItem) {
        this.requiredRecipients = item.requiredAttendees.map(a => <orgmap.UserItem>{
            displayName: a.name,
            titleName: "",
            deptName: a.address,
            email: a.address
        });

        this.optionalRecipients = item.optionalAttendees.map(a => <orgmap.UserItem>{
            displayName: a.name,
            titleName: "",
            deptName: a.address,
            email: a.address
        });
    }

    mountRecurrence(recurrence: any) {
        var recurringEndType = '1';

        if (recurrence.hasEnd) {
            if (recurrence.numberOfOccurrences != null) {
                recurringEndType = '2'
            } else {
                recurringEndType = '3'
            }
        }

        this.currentReccuringOption = <reserve.CalendarItemRecurrence>{
            daysOfTheWeekArray: recurrence.daysOfTheWeekArray,
            startDate: uGetDateFormat('yyyy-MM-dd', IEDate(recurrence.startDate)),
            endDate: recurrence.endDate != null ? uGetDateFormat('yyyy-MM-dd', IEDate(recurrence.endDate)) : null,
            interval: recurrence.interval,
            numberOfOccurrences: recurrence.numberOfOccurrences,
            ui_endType: recurringEndType,
            ui_recurringType: this.currentItem?.pidLidRecurrenceType || 1,
            dayOfMonth: recurrence.dayOfMonth || "",
            monthValue: recurrence.monthValue || 1
        }

        this.updateRecurrenceDesc();
    }

    emitContent(): string {
        const editor: editor.IEditorProxy = this.$refs.editor as any 
        return editor.getEditor().getBodyValue();
    }

    fileUploadStatusChaged(data: any) {
        if (data === 'uploading') {
            this.isFileUploading = true
        } else {
            this.isFileUploading = false
        }
    }

    onCancelClick() {     
        handleClose(this.isPopup);
    }

    async checkRequestValidate(comCode: string, itemStore: 'EWS' | 'DB' | 'FAVORITE', requestResource: string, requestItem: reserve.CalendarItem): Promise<boolean> {        
        var current = new Date();
        var { reserveId } = this.$route.query;
        var checkStart = IEDate(`${requestItem.start} ${requestItem.starttime}`);
        var checkEnd = IEDate(`${requestItem.end} ${requestItem.endtime}`);       

        const toSeconds = (timeStr: string): number => {
            const [h, m, s] = timeStr.split(':').map(Number);
            return h * 3600 + m * 60 + s;
        };

        const baseStart = toSeconds('08:00:00');
        const baseEnd = toSeconds('17:30:00');

        const startTime = toSeconds(requestItem.starttime);
        const endTime = toSeconds(requestItem.endtime);

        if (
            requestResource === 'bc9cc658-9636-4049-a4b9-05042827f018' ||
            requestResource === '3d579d4c-77a7-46db-b9d5-de02407fc60a' ||
            requestResource === '4b13daec-3cc5-4d2d-ae31-23126a26e7fb' ||
            requestResource === 'e2add1c7-f461-41aa-9712-a428563f87d1' ||
            requestResource === '2534945a-d93e-4fec-8377-8603f5e6b71f' ||
            requestResource === '0a039b7c-a036-4652-a0bd-4eca65d56569' ||
            requestResource === 'f48e1b57-6ece-4d2e-b502-03626564a9db' ||
            requestResource === 'e20eaeb7-3237-426b-a2d5-f75ee7bf12a7' ||
            requestResource === '3f23f181-75e4-40de-84f4-ffc4cc9ae32a' ||
            requestResource === '37bf43be-3b2c-4879-a7b5-ee3ab238f2a3' ||
            requestResource === 'bad1a41d-9716-4ae6-8473-8bd734167afb' ||
            requestResource === 'a749e352-e2ce-459c-a703-f761223d731c' ||
            requestResource === '69dad044-b6d5-41d2-a3df-5207ea856e46' ||
            requestResource === '45cc4d96-10d9-44cd-9931-af896933c4e0' ||
            requestResource === 'a6166df2-dd87-4299-9265-bd8420dbf319' ||
            requestResource === '63df2c7b-4160-446a-ab2b-1b28173dc081' ||
            requestResource === 'ff74ca41-8b5b-4e14-9eed-1321e8a754e6' ||
            requestResource === 'f514aef5-4376-4931-9055-a907d3a39c55' ||
            requestResource === 'b4d746c4-6963-494c-b95d-a6dca2fdc055'
        ) {
            if (
                requestItem.start !== requestItem.end || startTime > endTime || startTime < baseStart ||endTime > baseEnd
            ) {
                await(window as any).alertAsync('공용휴대폰 대여는 08:30 ~ 17:30까지만 가능합니다.');
                return false;
            }
        }

        if(requestItem.subject == null || requestItem.subject.trim().length == 0) {
            await (window as any).alertAsync(this.$t('예약_제목을_입력하세요').toString());
            return false;
        }

        // if(checkStart.getTime() <= current.getTime()) {
        //     await (window as any).alertAsync(this.$t('회의실_과거예약').toString());        
        //     return false;
        // }
        // 반복 설정 없을때
        if(!this.isAlldayEvent && this.currentReccuringOption == null){
            if(checkStart.getTime() <= current.getTime()) {
                await (window as any).alertAsync(this.$t('회의실_과거예약').toString());        
                return false;
            }
            // 반복 설정 없을때
        }else{
            // if(checkStart.getDate() < current.getDate()  && this.currentReccuringOption == null) {
            //     await (window as any).alertAsync(this.$t('회의실_과거예약').toString());        
            //     return false;
            // }

            const startDate = new Date(checkStart.getFullYear(), checkStart.getMonth(), checkStart.getDate());
            const currentDate = new Date(current.getFullYear(), current.getMonth(), current.getDate());
            if (startDate.getTime() < currentDate.getTime()) {
                await (window as any).alertAsync(this.$t('회의실_과거예약').toString());
                return false;
            }

        }

        if ((checkStart.getTime() - checkEnd.getTime())/(1000 * 60) > 1440) {
            return true;
        }

        var availability = await api.resource.getResourceAvailable({              
            itemStore: itemStore,
            resource: requestResource,
            start: checkStart,
            end: checkEnd,       
            originalId: this.mode == ReserveResourceMode.Edit ? reserveId.toString() : undefined              
        })

        if(!availability.available) {
            await (window as any).alertAsync(this.$t('회의실_사용시간중복').toString());
            return false;
        }

        return true;
    }

    getRequestModel(requestResource: ewsResource.EwsResourceItem | reserve.DbResourceItem, myCalendarFolderId: string | null): reserve.CalendarItem {
        var requestItem: reserve.CalendarItem = {
            parentId: '',
            //attachments: [] as any,
            hasAttachments: false,
            body: '',
            location: '',
            subject: '',
            status: 'S0',
            appointmentType: 'Single',
            pidLidRecurrenceType: 1,
            ui_resourceArray: [] as Array<any>,
            resourceId: null,
            resources: [] as Array<any>,
            recurrence: null,
            code: this.uploaderConfig.code,
            codeValues: this.uploaderConfig.codeValues,
            importance: 'Normal',
            isCancelled: false,
            isRecurring: false,
            isAllDayEvent: false,
            isAllowUpdate: true,
            start: uGetDateFormat('yyyy-MM-DDTH:i:00', new Date()),
            end: uGetDateFormat('yyyy-MM-DDTH:i:00', new Date()),
            requiredAttendees: [] as any,
            optionalAttendees: [] as any,
            isReadyData: false,
            delegateUser: '',
            reminderMinutesBeforeStart: 15            
        } as reserve.CalendarItem;

        requestItem.subject = this.confSubject;
        requestItem.body = this.emitContent();                       
        requestItem.importance = "Normal";
        requestItem.appointmentType = 'Single';
        requestItem.isRecurring = false;
        requestItem.reminderMinutesBeforeStart
        requestItem.isAllDayEvent = this.isAlldayEvent;
        requestItem.start = this.eventStart;
        requestItem.starttime = this.isAlldayEvent ? "00:00:00" : this.eventStartTime;
        requestItem.end = this.eventEnd;
        requestItem.endtime = this.isAlldayEvent ? "00:00:00" : this.eventEndTime;
        requestItem.location = requestResource.displayName;
        requestItem.requiredAttendees = this.requiredRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });
        requestItem.optionalAttendees = this.optionalRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });

        //alert(requestItem.end + "/getRequestModel/" + requestItem.endtime);
                    
        if(requestResource.itemStore === 'EWS') {
            requestItem.parentId = myCalendarFolderId;
            requestItem.requiredAttendees.push({
                name: requestResource.displayName,
                address: requestResource.address
            }); 
        } else {            
            requestItem.parentId = (requestResource as reserve.DbResourceItem).folderId?.toString();
            requestItem.organizer = {
                name: this.user.displayName,
                address: this.user.email
            }
        }                 

        // 기존에 있었던 로직(isAllDayEvent 로직변경으로 주석처리)
        // if(requestItem.isAllDayEvent && (requestItem.start === requestItem.end)) {            
        //     var changeEnd = IEDate(requestItem.end);
        //     changeEnd.setDate(changeEnd.getDate() + 1);
        //     requestItem.end = uGetDateFormat('yyyy-MM-dd', changeEnd);
        // }

        if(this.currentReccuringOption != null) {
            requestItem.isRecurring = true;
            requestItem.recurrence = this.currentReccuringOption;
            requestItem.appointmentType = 'RecurringMaster';           
        } else {
            requestItem.isRecurring = false;
            requestItem.recurrence = null;
        }
            
        return requestItem;
    }

    async onReserveClick() {            
        if(this.curResourceItem == null) {
            await (window as any).alertAsync(this.$t('선택된_자원이_없습니다'));
            return;
        }

        // if(this.curResourceItem.displayName === '4F_적극룸(10석, 화상회의, 빔프로젝터)'
        // || this.curResourceItem.displayName === '4F_A(6석)'
        // || this.curResourceItem.displayName === '4F_B(6석,컨퍼런스콜)'
        // || this.curResourceItem.displayName === '4F_C(6석)'
        // || this.curResourceItem.displayName === '4F_D(6석,빔프로젝터)'
        // || this.curResourceItem.displayName === '4F_F(6석)'
        // ){

          if (
            this.curResourceItem.uniqueId === 'bc9cc658-9636-4049-a4b9-05042827f018' ||
            this.curResourceItem.uniqueId === '3d579d4c-77a7-46db-b9d5-de02407fc60a' ||
            this.curResourceItem.uniqueId === '4b13daec-3cc5-4d2d-ae31-23126a26e7fb' ||
            this.curResourceItem.uniqueId === 'e2add1c7-f461-41aa-9712-a428563f87d1' ||
            this.curResourceItem.uniqueId === '2534945a-d93e-4fec-8377-8603f5e6b71f' ||
            this.curResourceItem.uniqueId === '0a039b7c-a036-4652-a0bd-4eca65d56569' ||
            this.curResourceItem.uniqueId === 'f48e1b57-6ece-4d2e-b502-03626564a9db' ||
            this.curResourceItem.uniqueId === 'e20eaeb7-3237-426b-a2d5-f75ee7bf12a7' ||
            this.curResourceItem.uniqueId === '3f23f181-75e4-40de-84f4-ffc4cc9ae32a' ||
            this.curResourceItem.uniqueId === '37bf43be-3b2c-4879-a7b5-ee3ab238f2a3' ||
            this.curResourceItem.uniqueId === 'bad1a41d-9716-4ae6-8473-8bd734167afb' ||
            this.curResourceItem.uniqueId === 'a749e352-e2ce-459c-a703-f761223d731c' ||
            this.curResourceItem.uniqueId === '69dad044-b6d5-41d2-a3df-5207ea856e46' ||
            this.curResourceItem.uniqueId === '45cc4d96-10d9-44cd-9931-af896933c4e0' ||
            this.curResourceItem.uniqueId === 'a6166df2-dd87-4299-9265-bd8420dbf319' ||
            this.curResourceItem.uniqueId === '63df2c7b-4160-446a-ab2b-1b28173dc081' ||
            this.curResourceItem.uniqueId === 'ff74ca41-8b5b-4e14-9eed-1321e8a754e6' ||
            this.curResourceItem.uniqueId === 'f514aef5-4376-4931-9055-a907d3a39c55' ||
            this.curResourceItem.uniqueId === 'b4d746c4-6963-494c-b95d-a6dca2fdc055'
        ) {
            if(this.currentReccuringOption != null) {
                await(window as any).alertAsync('공용휴대폰은 반복설정 예약이 불가합니다.');
                return false;
            }
        }

        if(this.curResourceItem.uniqueId === '595ffe2e-815b-4fae-bacb-e5c96d295c09'
            || this.curResourceItem.uniqueId === 'b2cd57a6-04fa-4c6b-b9db-9f4a6b6ec424'
            || this.curResourceItem.uniqueId === '0d7dfa39-8735-4a45-b062-f884a4650961'
            || this.curResourceItem.uniqueId === 'd976c698-3ddf-47d1-8485-a71ddb8514b9'
            || this.curResourceItem.uniqueId === 'f131f545-0818-43af-894e-b4fd73bc545a'
            || this.curResourceItem.uniqueId === '44f37c10-796f-4711-8847-e49558d8fd5e'
        ){
            if( ['2025-06-23','2025-06-24','2025-06-25'].includes(this.eventStart) ||
                ['2025-06-23','2025-06-24','2025-06-25'].includes(this.eventEnd)){
                await (window as any).alertAsync("6월 23일 ~ 25일의 기간은 사무공간 레이아웃 공사로 인하여 예약이 불가합니다.");
                this.isLoading = false;
                return;
            }
        }

        // if(this.curResourceItem.displayName === '6F_우보천리룸(29석, 화상회의, 빔프로젝터)') {
        if(this.curResourceItem.uniqueId === 'a5e66f54-4383-46e0-b51e-b5cdab96d464') {
            if(this.user.personCode !== 'COM_01_222081' && this.user.personCode !== 'COM_01_209065') {
                await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 사용자만 예약가능합니다.').toString());
                this.isLoading = false;
                return;
            }
        }

        // if (this.curResourceItem.displayName === 'B1_Display Room(12석)') {
        if (this.curResourceItem.uniqueId === 'e68918c9-a3cc-4010-80bf-0ad2252a0f3f') {
            if (this.user.deptCode !== 'COM_02_34000' && this.user.deptCode !== 'COM_02_32101'
                && this.user.deptCode !== 'COM_02_32102' && this.user.deptCode !== 'COM_02_32103' && this.user.deptCode !== 'COM_02_SO100062'
                && this.user.deptCode !== 'COM_02_SO100066' && this.user.deptCode !== 'COM_02_SO100106'
            ) {
                await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 그룹의 사용자만 예약가능합니다.').toString());
                this.isLoading = false;
                return;
            }
        }

        // if (this.curResourceItem.displayName === '석오빌딩 10F 디스플레이룸 (8석,모니터)') {
        // 이름 변경 석오빌딩 10F 디스플레이룸 (8석,모니터) -> Partner Studio
        if (this.curResourceItem.uniqueId === 'f5c57b31-e18d-47a7-be71-3675b5692d79') {
            if(this.user.personCode !== 'COM5_S221057' && this.user.personCode !== 'COM5_S221096'  && this.user.personCode !== 'COM5_S223054'
                && this.user.personCode !== 'COM5_S223108' && this.user.personCode !== 'COM5_S224040' && this.user.personCode !== 'COM5_S224047'
                && this.user.personCode !== 'COM5_S221049' && this.user.personCode !== 'COM5_S221185'
                && this.user.personCode !== 'COM5_S225039' && this.user.personCode !== 'COM5_S223011'
                && this.user.personCode !== 'COM5_S226023' && this.user.personCode !== 'COM5_S226010'
                && this.user.personCode !== 'COM5_S225052' && this.user.personCode !== 'COM5_S220186'
                && this.user.personCode !== 'COM5_S222074'
            ) {
                await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 사용자만 예약가능합니다.').toString());
                this.isLoading = false;
                return;
            }
        }

        // if (this.curResourceItem.displayName === '3F_센서리라운지') {
        // 부서 : COM_02_36000(향료연구센터),  COM_02_32054(퍼스널케어1팀), COM_02_32058(퍼스널케어2팀)  
        // 퍼스널케어연구소(COM_02_33800), 헤어연구팀(COM_02_32054), 바디연구팀(COM_02_SO100108), 덴탈연구팀(COM_02_32058), CT연구팀(COM_02_32065), 향료연구센터(COM_02_36000)
        // 2026.01.15 COM_02_SO100026(U'r팀) 권한 회수
        if (this.curResourceItem.uniqueId === '03c4d587-d931-4458-be1d-fe61fb393d9a') {
            if (this.user.deptCode !== 'COM_02_36000' && this.user.deptCode !== 'COM_02_32054'
                && this.user.deptCode !== 'COM_02_32058' && this.user.deptCode !== 'COM_02_33800'
                && this.user.deptCode !== 'COM_02_SO100108' && this.user.deptCode !== 'COM_02_32065' ) {
                await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 그룹의 사용자만 예약가능합니다.').toString());
                this.isLoading = false;
                return;
            }
        }

        const allowedPersonCodes = ['COM_01_223917', 'COM_01_223918', 'COM_01_223915', 'COM_01_223932','COM_01_300521','COM_01_300523'];

        // if (
        //     ['애터미룸', '창조룸', '인큐베이션룸', '1F_고문실'].includes(this.curResourceItem.displayName) &&
        //     !allowedPersonCodes.includes(this.user.personCode)
        // )
        if (
            ['c08e0b98-2bad-46e1-8f44-476d35f3da25',
            '088de8e2-5db2-4027-9db2-951632849c81',
            '1c085d1b-dd9c-4a43-8905-85e6ef5e8fbd',
                '6c37cba3-59df-4bbf-884e-198c6bb0bc7c'].includes(this.curResourceItem.uniqueId) &&
            !allowedPersonCodes.includes(this.user.personCode)
        )
        {
        await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 사용자만 예약가능합니다.').toString());
        this.isLoading = false;
        return;
        }

        const allowedPersonCodes2 = ['COM_02_205040', 'COM_02_218269', 'COM_01_225138', 'COM_02_225244'];

        if (this.curResourceItem.uniqueId === '9d3a24a4-a8a8-47e4-b78b-023869db5e76') {
            if (!allowedPersonCodes2.includes(this.user.personCode)) {
                await (window as any).alertAsync(this.$t('선택하신 회의실은 지정 된 사용자만 예약가능합니다.').toString());
                this.isLoading = false;
                return;
            }
        }

        var requestResource = this.curResourceItem;
        this.isLoading = true;
        let requestItem = this.getRequestModel(requestResource, this.myCalendarFolderId);

        var validate: boolean = true;

        if(!this.isAlldayEvent) {
            validate = await this.checkRequestValidate(this.initCompany, requestResource.itemStore, requestResource.address, requestItem);
        } 
        else
        {
            requestItem.starttime = '00:00:00'
            //alert(requestItem.endtime);
            
            requestItem.endtime = '00:00:00'   
            //requestItem.endtime = '23:30:00'  
                            
            // //let eDate = IEDate(requestItem.end);
            // //alert(requestItem.end);
            // var eDate = IEDate(requestItem.end);
            // eDate.setDate(eDate.getDate() + 1);
            // requestItem.end = eDate.toISOString().slice(0, 10);


            validate = await this.checkRequestValidate(this.initCompany, requestResource.itemStore, requestResource.address, requestItem);
        }
    
        if(validate) {
            this.createEvent({ 
                item: requestItem, 
                itemStore: requestResource.itemStore 
            }).then(async res => {
                await (window as any).alertAsync(this.$t('예약신청_완료').toString());
                handleClose(this.isPopup, true);
            })    
        } else {                
            this.isLoading = false;
        }                                         
    }

    updateRecurrenceDesc() {        
        if(this.currentReccuringOption != null) {
            var constMonthArray: string[] = [
                this.$t('W_1M').toString(),
                this.$t('W_2M').toString(),
                this.$t('W_3M').toString(),
                this.$t('W_4M').toString(),
                this.$t('W_5M').toString(),
                this.$t('W_6M').toString(),
                this.$t('W_7M').toString(),
                this.$t('W_8M').toString(),
                this.$t('W_9M').toString(),
                this.$t('W_10M').toString(),
                this.$t('W_11M').toString(),
                this.$t('W_12M').toString()
            ];

            var constWeekArray: string[] = [
                this.$t('W_Schedule_Sunday').toString(),
                this.$t('W_Schedule_Monday').toString(),
                this.$t('W_Schedule_Tuesday').toString(),
                this.$t('W_Schedule_Wednesday').toString(),
                this.$t('W_Schedule_Thursday').toString(),
                this.$t('W_Schedule_Friday').toString(),
                this.$t('W_Schedule_Saturday').toString()
            ];

            var dispTimeArray = uGetTimeArray([
                this.$t('W_AM').toString(),
                this.$t('W_PM').toString()
            ]);

            var idxStartTime = dispTimeArray.findIndex(t => t.value === this.eventStartTime);
            var idxEndTime = dispTimeArray.findIndex(t => t.value === this.eventEndTime);
            var dispHour = (idxEndTime - idxStartTime) * 0.5;
            var timeSpan = this.isAlldayEvent ?
                this.$t('W_Schedule_Allday').toString() :
                `${dispTimeArray[idxStartTime].text} ~ ${dispTimeArray[idxEndTime].text} (${dispHour}${this.$t('W_Hour').toString()})`;
            var prefix = "";

            switch (this.currentReccuringOption.ui_recurringType) {
                case 1:
                    prefix = this.$t('매N일마다').toString().replace('{INTERVAL}', this.currentReccuringOption.interval.toString());
                    break;
                case 2:
                    var dayJoin = this.currentReccuringOption.daysOfTheWeekArray?.map(i => constWeekArray[i]).join(",");
                    prefix = this.$t('매N주').toString()
                        .replace('{INTERVAL}', this.currentReccuringOption.interval.toString())
                        .replace('{DAYJOINS}', dayJoin || "");
                    break;
                case 3:
                    prefix = this.$t('매N월').toString()
                        .replace('{INTERVAL}', this.currentReccuringOption.interval.toString())
                        .replace('{DAYOFMONTH}', this.currentReccuringOption.dayOfMonth?.toString() || "");
                    break;
                case 4:
                    prefix = this.$t('매년N월').toString()
                        .replace('{MONTH}', constMonthArray[this.currentReccuringOption.monthValue - 1])
                        .replace('{DAYOFMONTH}', this.currentReccuringOption.dayOfMonth?.toString() || "");
                    break;
            }

            this.recurrenceDesc = this.$t('반복일정_설명').toString().replace('{PREFIX}', prefix).replace('{TIMESPAN}', timeSpan);

            switch (this.currentReccuringOption.ui_endType) {
                case "1":
                    this.recurrenceIntervalDesc = this.$t('N부터_계속반복됩니다').toString().replace('{START}', this.currentReccuringOption.startDate);
                    break;
                case "2":
                    this.recurrenceIntervalDesc = this.$t('N회_반복후종료').toString().replace('{OCCURRENCE_NUMBER}', this.currentReccuringOption.numberOfOccurrences?.toString() || "");
                    break;
                case "3":
                    this.recurrenceIntervalDesc = `${this.currentReccuringOption.startDate} ~ ${this.currentReccuringOption.endDate}`;
                    break;
            }
        }
    }

    onRecurringClick() {
        this.showRecurringOption = true;
    }

    onRecurringOptionSave(option: reserve.CalendarItemRecurrence) {
        this.currentReccuringOption = option;
        this.currentReccuringOption.startDate = this.eventStart;
        this.updateRecurrenceDesc();
        this.showRecurringOption = false;
    }

    onReccuringOptionClose() {
        this.showRecurringOption = false;
    }

    onRemoveRecurrenceClick() {
        this.currentReccuringOption = null;
    }

    async onUpdateReserveClick() {
        if(this.curResourceItem == null) {
            await (window as any).alertAsync(this.$t('선택된_자원이_없습니다'));
            return;
        }
            
        var { resource } = this.$route.query;
        this.isLoading = true;
        
        var updateResource = resource.toString();
        var requestItem = { ...this.currentItem } as reserve.CalendarItem;               

        requestItem.subject = this.confSubject;
        requestItem.status = 'S0',
        requestItem.isAllDayEvent = this.isAlldayEvent;
        requestItem.start = this.eventStart;
        requestItem.body = this.emitContent();
        requestItem.starttime = this.isAlldayEvent ? "00:00:00" : this.eventStartTime;
        requestItem.end = this.eventEnd;
        requestItem.endtime = this.isAlldayEvent ? "00:00:00" : this.eventEndTime;
        requestItem.requiredAttendees = this.requiredRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });
        requestItem.optionalAttendees = this.optionalRecipients.map(r => <ewsResource.EwsRecipient>{ name: r.displayName, address: r.email });        
        
        requestItem.code = this.uploaderConfig.code;
        requestItem.codeValues = this.uploaderConfig.codeValues;

        // 기존에 있었던 로직(isAllDayEvent 로직변경으로 주석처리)
        // if(requestItem.isAllDayEvent && (requestItem.start === requestItem.end)) {
        //     var changeEnd = IEDate(requestItem.end);
        //     changeEnd.setDate(changeEnd.getDate() + 1);
        //     requestItem.end = uGetDateFormat('yyyy-MM-dd', changeEnd);
        // }

        if (this.currentReccuringOption != null) {
            requestItem.isRecurring = true;
            requestItem.recurrence = this.currentReccuringOption;
        }

        if(this.masterOccurrence !== 'master' && this.curResourceItem.itemStore === 'DB') {
            requestItem.db_masterID = Number(requestItem.id);
            requestItem.id = this.sequence.toString();
            requestItem.appointmentType = 'Exception';        
        }

        var validate: boolean = true;

        if(!this.isAlldayEvent) {
            validate = await this.checkRequestValidate(this.initCompany, this.curResourceItem.itemStore, updateResource, requestItem);
        }
        else
        {
            requestItem.starttime = '00:00:00'
            //requestItem.endtime = '00:00:00'    
             
            //requestItem.endtime = '23:30:00'    
            //requestItem.endtime = '22:00:00'    
                            
            // //let eDate = IEDate(requestItem.end);
            // //alert(requestItem.end);
            // var eDate = IEDate(requestItem.end);
            // //alert("eDate=" + eDate);
            // eDate.setDate(eDate.getDate() + 1);
            // requestItem.end = eDate.toISOString().slice(0, 10);
            //alert("END=" + requestItem.end);

            validate = await this.checkRequestValidate(this.initCompany, this.curResourceItem.itemStore, updateResource, requestItem);
        }

        //alert("validate_update=" + validate);

        if (validate) {
            this.updateEvent({ 
                    item: requestItem, 
                    itemStore: this.curResourceItem.itemStore,
                    masterOccurrence: this.masterOccurrence 
            }).then(async res => {
                await (window as any).alertAsync(this.$t('예약신청_업데이트').toString());
                handleClose(this.isPopup, true);
            })
        } else {
            this.isLoading = false;
        }
    }

    async onCancelReserveClick() {   
        // if(this.currentItem && this.curResourceItem != null) {
        //     var itemStore = this.curResourceItem.itemStore
        //     var id = this.currentItem.id
        //     var currentItem = this.currentItem

        //     this.$confirm(this.$t('예약삭제_확인').toString(), '', undefined, Vue.prototype.$confirmOptions).then((res: boolean) =>{
        //         if(res) {
        //             this.isLoading = true;
        //             var cancelPromise: Promise<any> = Promise.resolve();
        
        //             if(itemStore === 'EWS') {
        //                 cancelPromise = this.cancelEwsEvent(id);
        //             } else {
        //                 cancelPromise = this.cancelDbEvent(currentItem);
        //             }
        
        //             cancelPromise.then(res => {
        //                 alert(this.$t('예약삭제_안내').toString());
        //                 handleClose(this.isPopup, true);
        //             })             
        //         }
            
        //         Vue.prototype.$confirmOptions.html = ''
        //     })
        // }
        
        if(this.currentItem && this.curResourceItem != null && await (window as any).confirmAsync(this.$t('예약삭제_확인').toString())) {
            this.isLoading = true;
            var cancelPromise: Promise<any> = Promise.resolve();

            if(this.curResourceItem.itemStore === 'EWS') {
                cancelPromise = this.cancelEwsEvent(this.currentItem.id);
            } else {
                cancelPromise = this.cancelDbEvent(this.currentItem);
            }

            cancelPromise.then(async res => {
                await (window as any).alertAsync(this.$t('예약삭제_안내').toString());
                handleClose(this.isPopup, true);
            })                
        }
    }

    onNewWindowClick() {
        openReserveWindow({
            comCode: this.initCompany,
            category: this.initCategory,
            resource: this.curResourceItem?.address
        })   
    }
    
    onFileRemoved(file: ewsResource.EwsAttachment, idx: number) {
        this.ewsAttachments.splice(idx, 1);        
    }
}